import * as React from "react";
import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, ChevronsUpDown, LogOut, User } from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../../components/ui/sidebar";
import { UserContext } from "../../crud/user/context";
import { Urls } from "../../domain/urls";
import { useCookie } from "../../utils/hooks";
import { VIEWEDACCOUNT_COOKIE } from "../../cookieUtils";
import { useLogoutMutation } from "../../crud/auth/hooks";

export const NavUser = ({
  className,
}: {
  className?: string;
}): React.ReactElement => {
  const { isMobile } = useSidebar();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const logoutMutation = useLogoutMutation();
  const [, setViewedAccountCookie] = useCookie(VIEWEDACCOUNT_COOKIE);

  return (
    <SidebarMenu className={className}>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.profile.avatarUrl} alt={user.fullName} />
                <AvatarFallback className="rounded-lg">
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.fullName}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={12}
          >
            <DropdownMenuGroup>
              <DropdownMenuItem
                asChild
                className="cursor-pointer hover:bg-muted"
              >
                <Link to={Urls.Profile}>
                  <BadgeCheck />
                  Profile
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer hover:bg-muted"
              onClick={() => {
                setViewedAccountCookie(undefined);
                logoutMutation.mutate({});
                navigate(`/${Urls.Login}`);
              }}
            >
              <LogOut className="mr-1" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
